import { useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Pagination from '@mui/material/Pagination';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';

import { fDateTime } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { getEmployeeAuditLog } from 'src/redux/slices/audit';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';

import Iconify from 'src/components/iconify';

import { AuditAction, AuditType, IAuditListItem } from 'src/types/audit';

import { buildLoanActivityMessage } from '../loan-agreement/utils';

const ACTIVITY_LOG_PAGE_SIZE = 10;

type Props = {
  employeeId: string;
};

function buildActivityMessage(item: IAuditListItem): string {
  const { type, action, after } = item;

  if (type === AuditType.EMPLOYEE) {
    if (action === AuditAction.CREATED) return 'Employee record created';
    if (action === AuditAction.DELETED) return 'Employee record deleted';

    if (action === AuditAction.UPDATED) {
      if (after?.status === 'disabled' && after?.employmentEndReason) {
        return `Employment ended — ${after.employmentEndReason}`;
      }

      const changedFields = Object.keys(after ?? {});
      return changedFields.length > 0
        ? `Employee details updated (${changedFields.join(', ')})`
        : 'Employee details updated';
    }
  }

  if (type === AuditType.ADVANCED_PAYMENTS) {
    if (action === AuditAction.CREATED) {
      return `Advanced payment of ${fCurrencyRupees(after?.amountInCents ?? 0)} recorded`;
    }
    if (action === AuditAction.UPDATED) return 'Advanced payment updated';
    if (action === AuditAction.DELETED) return 'Advanced payment deleted';
  }

  return buildLoanActivityMessage(item) ?? `${type} ${action.toLowerCase()}`;
}

function activityIcon(action: AuditAction) {
  switch (action) {
    case AuditAction.CREATED:
      return 'eva:plus-circle-fill';
    case AuditAction.DELETED:
      return 'eva:trash-2-fill';
    default:
      return 'eva:edit-2-fill';
  }
}

export default function EmployeeActivityLog({ employeeId }: Props) {
  const dispatch = useAppDispatcher();

  // Explicit cast: TypeScript silently loses this nested slice's inferred
  // type (no diagnostic elsewhere) when selecting through the full
  // combined+persisted RootState, so noImplicitAny only surfaces at the
  // first real usage below (.map). Shape matches audit.ts's IInitialState.
  const { status, count, page, pageCount, items } = useAppSelector(
    (state) => state.audit.employeeAuditLog
  ) as {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IAuditListItem[];
  };

  const isLoading = status === 'loading';

  useEffect(() => {
    if (employeeId) {
      dispatch(getEmployeeAuditLog({ employeeId, page: 1, limit: ACTIVITY_LOG_PAGE_SIZE }));
    }
  }, [dispatch, employeeId]);

  const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
    dispatch(getEmployeeAuditLog({ employeeId, page: newPage, limit: ACTIVITY_LOG_PAGE_SIZE }));
  };

  return (
    <Card sx={{ p: 3 }}>
      <CardHeader title="Activity Log" sx={{ p: 0, mb: 2 }} />

      {isLoading && (
        <Stack spacing={2}>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} variant="rounded" height={56} />
          ))}
        </Stack>
      )}

      {!isLoading && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No activity yet.
        </Typography>
      )}

      {!isLoading && items.length > 0 && (
        <Stack spacing={2}>
          {items.map((item) => (
            <Stack key={item._id} direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'background.neutral',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={activityIcon(item.action)} width={18} />
              </Box>

              <Stack flexGrow={1} spacing={0.5}>
                <Typography variant="body2">{buildActivityMessage(item)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {item.actorEmail}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {fDateTime(item.createdAt)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {count > 0 && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.disabled">
            {count} {count === 1 ? 'activity' : 'activities'}
          </Typography>

          {pageCount > 1 && (
            <Pagination
              size="small"
              color="primary"
              page={page}
              count={pageCount}
              onChange={handleChangePage}
            />
          )}
        </Stack>
      )}
    </Card>
  );
}
